// About page content — sourced from https://www.worldfarmers.org/about/
// All copy is faithful to the source (lightly edited for spacing/typos).
// All imagery is locally downloaded from worldfarmers.org (no stock / no AI).

export const ABOUT_HERO = {
  eyebrow: "",
  title: "About World Farmers",
  lede: "Building farmer capacity since 1984.",
  image: "/images/wf/farm-017.jpg",
  imageAlt:
    "A smiling farmer standing in a sunlit field beneath an open sky at the Flats Mentor Farm.",
};

// Short lead sentence shown directly beneath the hero.
export const ABOUT_LEAD =
  "World Farmers support immigrant and refugee farmers to build thriving, independent farm businesses \u2014 rooted in cross-cultural co-learning on shared-use landsites throughout Central Massachusetts.";

export const ABOUT_OVERVIEW = {
  eyebrow: "",
  title: "Who We Are",
  paragraphs: [
    "World Farmers advocates for and supports small-scale diversified farmers from farm to market. We provide the land, farm infrastructure, and mentoring necessary for immigrant and refugee communities to build viable farming businesses and claim their own food sovereignty.",
    "Since 1984, our program has been a place where immigrants and refugees from agrarian backgrounds find an opportunity to reconnect with farming as a way of life, enhance their agricultural production knowledge, and evaluate its sustainability for themselves and their families while preserving their cultural identity.",
    "Over the last four decades we have refined our program to offer greater support to the farmers we serve including training, hands-on assistance, and facilitated mentorship spaces to build the capacity of each farmer to operate individual farming enterprises. At present, we support 275 farmers, representing 25 countries across the World, producing over 135 acres of vegetables in Massachusetts and supplying wholesale and retail markets throughout New England.",
    "World Farmers\u2019 model is not a traditional food incubator program, growers association, or food hub; our services encompass all those roles and more. World Farmers conducts no farmer recruitment; all farmers who have participated in the program have learned about the opportunity through word of mouth. Our early work was developed out of need, and our program development continues to be driven and shaped by farmers. World Farmers\u2019 program encourages natural mentoring across farmers and cultures, cultivating shared space among individuals of like-backgrounds to learn from each other.",
    "Many of the farmers in our program have shared they never imagined there would be an opportunity like this when they came to this country: access to land, trainings in new growing practices for this climate, and support in building a business in farming. These have made a huge impact on their ability to adapt and thrive. Just as important as our programming and services is the simple access to a safe space to learn from their neighbors, feed their families, heal, rebuild their cultural foundations, and teach the next generation all that agriculture can provide.",
  ],
  image: "/images/wf/vlai/vlai-3871.jpg",
  imageAlt:
    "A farmer pushes a wheelbarrow of harvested greens down a farm road lined with hoop houses.",
};

export const ABOUT_MISSION = {
  eyebrow: "Our mission",
  statement:
    "World Farmers\u2019 mission is to support small-scale farmers in agricultural production and successful marketing practices, and to connect culturally relevant produce to viable markets.",
  body:
    "World Farmers provides mentoring, training, and hands-on assistance when working with each farmer to build the capacity needed to operate individual farming enterprises. Participating farmers are skilled producers who come from agrarian backgrounds and seek to make a new life for themselves while preserving their cultural identity.",
  image: "/images/wf/vlai/vlai-3793.jpg",
  imageAlt:
    "A farmer in a sun hat walks through the field carrying a harvested gourd.",
};

export const ABOUT_HISTORY = {
  eyebrow: "",
  title: "Our History \u00b7 since 1984",
  body:
    "From a handful of refugee families planting familiar crops in unfamiliar soil to a thriving community representing over 25 countries, our story spans four decades of growth, resilience, and cross-cultural co-learning.",
  milestones: [
    { year: "1984", label: "Hmong refugee farmers begin growing at the Flats in Lancaster, MA." },
    { year: "1999", label: "Flats Mentor Farm was identified as a national model by the USDA and has served as a model for many beginning-farmer programs nationally." },
    { year: "2010", label: "World Farmers was founded." },
    { year: "Today", label: "Supporting 275 farmers across 9 program landsites." },
  ],
  cta: { label: "Explore our full history", href: "/history" },
  image: "/images/wf/vlai/vlai-3537.jpg",
  imageAlt:
    "A weathered wooden cart rests among tall grasses at the edge of a field.",
};

export const ABOUT_IMPACT = {
  eyebrow: "Our impact",
  title: "A model for beginning-farmer programs nationwide",
  stats: [
    { id: "farmers", value: "1,200+", label: "Farmers have grown at the Flats Mentor Farm" },
    { id: "own-land", value: "30%", label: "Have gone on to farm their own land" },
    { id: "women", value: "60%", label: "Of those independent farmers are women" },
  ],
  body:
    "To date, of the over 1,200 farmers who have had plots at Flats Mentor Farm, 30% have gone on to farm their own land \u2014 60% of whom are women. These independent farmers have established their operations across the country, from North Carolina to California.",
  highlight:
    "In 1999, Flats Mentor Farm was identified as a national model by the USDA and has served as a model for many beginning-farmer programs nationally.",
};

export const ABOUT_WHO = {
  eyebrow: "Where to go next",
  title: "Meet the people who make it possible",
  body:
    "World Farmers is powered by a dedicated team and board \u2014 and by the growers at the heart of it all. Get to know the people behind the fields.",
  cta: { label: "Meet the team", href: "/who-we-are" },
  cta2: { label: "Meet the farmers", href: "/meet-the-farmers" },
  image: "/images/wf/vlai/vlai-3744.jpg",
  imageAlt:
    "A farmer smiles while holding a long, freshly harvested gourd.",
};

// Explore hub — About acts as a landing page to the other key pages.
export const ABOUT_EXPLORE = {
  eyebrow: "Explore World Farmers",
  title: "Where to go next",
  links: [
    {
      id: "who-we-are",
      title: "Who We Are",
      body: "Meet the staff and board leading our mission.",
      href: "/who-we-are",
      image: "/images/wf/who-we-are.jpg",
      alt: "A smiling farmer carrying a large bag of freshly harvested corn beside a car at the edge of a field.",
    },
    {
      id: "history",
      title: "Our History",
      body: "Four decades of growth, from a few plots to a national model.",
      href: "/history",
      image: "/images/wf/farm-2611.jpg",
      alt: "A silhouette of a farmer working in a field during a vibrant, sun-drenched sunrise.",
    },
    {
      id: "programs",
      title: "Programs & Services",
      body: "The Flats Mentor Farm and our five-phase mentorship path.",
      href: "/programs",
      image: "/images/wf/vlai/vlai-3693.jpg",
      alt: "A field of kale with farmers working in the distance.",
    },
    {
      id: "impact",
      title: "Our Impact",
      body: "Results by the numbers, plus our annual reports.",
      href: "/impact",
      image: "/images/wf/farm-159.jpg",
      alt: "A farmer wearing a wide-brimmed hat bends over rows of crops at golden hour.",
    },
    {
      id: "farmers",
      title: "Meet the Farmers",
      body: "Stories from the growers who make the Flats what it is.",
      href: "/meet-the-farmers",
      image: "/images/wf/vlai/vlai-3766.jpg",
      alt: "A farmer harvests from tall trellised vines on a bright summer day.",
    },
    {
      id: "media",
      title: "Media",
      body: "Press coverage, announcements, and our blog.",
      href: "/media",
      image: "/images/wf/vlai/vlai-3647.jpg",
      alt: "A farmer in a floral sun hat looks up from harvesting peppers.",
    },
    {
      id: "where-to-buy",
      title: "Where to Buy Crops",
      body: "Find farm-fresh, culturally relevant produce near you.",
      href: "/where-to-buy",
      image: "/images/wf/vlai/vlai-3726.jpg",
      alt: "Rainbow chard glows in late-afternoon sunlight.",
    },
    {
      id: "support",
      title: "I Want to Support",
      body: "Help farmers access land, training, and markets.",
      href: "/support",
      image: "/images/wf/vlai/vlai-3895.jpg",
      alt: "A farmer in a sun hat picks peppers from a leafy plant.",
    },
  ],
};

// In the news — press mentions sourced from worldfarmers.org/about (selected,
// most recent & notable). type drives the icon: article | video | pdf.
export const ABOUT_PRESS = [
  {
    title:
      "In the fields that feed the Brookline Farmers\u2019 Market, vendors feel effects of federal uncertainty",
    publication: "The Brookline News",
    date: "September 14, 2025",
    href: "https://www.worldfarmers.org/wp-content/uploads/2025/09/Brookline_DONE.pdf",
    type: "pdf",
  },
  {
    title: "Congressman McGovern visits World Farmers in Sutton",
    publication: "The Millbury-Sutton Chronicle",
    date: "September 3, 2025",
    href: "https://www.worldfarmers.org/wp-content/uploads/2025/09/Congressman-McGovern-visits-World-Farmers-in-Sutton-2025.pdf",
    type: "pdf",
  },
  {
    title: "Local farmers, food organizations in limbo after federal food grant program ends",
    publication: "WBUR",
    date: "June 13, 2025",
    href: "https://www.worldfarmers.org/wp-content/uploads/2025/09/Wbur.pdf",
    type: "pdf",
  },
  {
    title: "\u201CWe lost everything\u201D: Lancaster refugee farm destroyed by flooding",
    publication: "CBS News Boston",
    date: "September 19, 2023",
    href: "https://www.cbsnews.com/boston/news/lancaster-refugee-flats-mentor-farm-destroyed-flooding/",
    type: "article",
  },
  {
    title: "Gov. Healey surveys flooded farm in Lancaster",
    publication: "NBC Boston",
    date: "July 20, 2023",
    href: "https://www.nbcboston.com/on-air/as-seen-on/gov-healey-surveys-flooded-farm-in-lancaster/3094564/",
    type: "video",
  },
  {
    title: "Area farmers call on Congress to make climate policy a priority",
    publication: "Greenfield Recorder",
    date: "March 12, 2023",
    href: "https://www.recorder.com/Area-farmers-call-on-Congress-to-make-climate-policy-a-priority-50225003",
    type: "article",
  },
  {
    title: "World Farmers organization devastated by floods",
    publication: "WCVB",
    date: "September 24, 2021",
    href: "https://www.wcvb.com/article/world-farmers-organization-devastated-by-floods/37725138",
    type: "article",
  },
  {
    title: "How a farmer\u2019s sales strategy turned her parents\u2019 garden into a family business",
    publication: "The Boston Globe",
    date: "May 4, 2021",
    href: "http://www.worldfarmers.org/wp-content/uploads/2021/12/May-4-2021BG.pdf",
    type: "pdf",
  },
  {
    title: "Supporting future farmers",
    publication: "Chronicle \u00b7 WCVB",
    date: "March 3, 2021",
    href: "https://www.youtube.com/watch?v=q3AhvbZFaDA",
    type: "video",
  },
  {
    title: "Health inside the box",
    publication: "Telegram & Gazette",
    date: "August 21, 2020",
    href: "https://www.telegram.com/story/news/coronavirus/2020/08/21/usda-food-box-program-enables-area-low-income-families-easier-access-to-produce/113948128/",
    type: "article",
  },
  {
    title: "Announcing the Winners of the Sustainable Business of the Year Awards 2020",
    publication: "Sustainable Business Network",
    date: "March 26, 2020",
    href: "https://myemail.constantcontact.com/Announcing-the-Winners-of-the-Sustainable-Business-of-the-Year-Awards-2020-.html?soid=1101207831942&aid=_fVf6yhICv4",
    type: "article",
  },
  {
    title: "Genocide Survivor to Massachusetts Farmer",
    publication: "BBC",
    date: "May 2016",
    href: "http://www.bbc.co.uk/programmes/p03t4pzp",
    type: "video",
  },
  {
    title: "With USDA Help, Rural African Farming Comes to Massachusetts",
    publication: "USDA",
    date: "2017",
    href: "https://www.usda.gov/media/blog/2013/03/18/usda-help-rural-african-farming-comes-rural-massachusetts",
    type: "article",
  },
  {
    title: "Land and Opportunity \u2014 A Life in Agriculture",
    publication: "Obama White House Archives",
    date: "August 4, 2014",
    href: "https://obamawhitehouse.archives.gov/blog/2014/08/04/land-and-opportunity-life-agriculture",
    type: "article",
  },
  {
    title: "Burundi Woman Models Future of Farming in Lancaster",
    publication: "WBUR",
    date: "July 29, 2014",
    href: "http://www.wbur.org/news/2014/07/29/fabiola-nizigiyimana-farming",
    type: "article",
  },
  {
    title: "Taking root in a new land \u2014 Immigrants renew a passion for planting",
    publication: "The Boston Globe",
    date: "June 30, 2005",
    href: "http://archive.boston.com/news/local/articles/2005/06/30/taking_root_in_a_new_land/",
    type: "article",
  },
];
